/**
 * Consent manager — full CMP orchestration.
 *
 * Reads/writes the fltconsent cookie, coordinates banner + panel,
 * sends consent proof to the server, and triggers pixel injection.
 */

import { announceToSR } from '../atoms/focus-trap';
import { showBanner, hideBanner, initBanner, removePolicyLink } from '../molecules/consent-banner';
import { openPanel, closePanel, readToggles, initPanel, handlePanelKeydown, isPanelVisible } from '../molecules/consent-panel';
import { applyConsent } from './pixel-injector';
import type { ConsentMap, ModuleConfig, FltConsentConfig } from '../types/consent.d';

const COOKIE_NAME = 'fltconsent';
const CATEGORIES: Array<keyof Omit<ConsentMap, 'necessary' | 'version' | 'ts'>> = [
  'analytics', 'advertising', 'personalization',
];

let config: ModuleConfig;
let consent: ConsentMap = { necessary: true };

export function initConsentManager(): void {
  const configEl = document.getElementById('flt-tm-config');
  const bannerEl = document.getElementById('flt-tm-banner');
  const panelEl = document.getElementById('flt-tm-panel');
  const overlayEl = document.getElementById('flt-tm-panel-overlay');

  if (!configEl) return;

  config = readConfig(configEl);
  consent = readCookie();

  initBanner(bannerEl);
  initPanel(panelEl, overlayEl, config.panelStyle);

  if (!config.cookiePolicyUrl && bannerEl) {
    removePolicyLink();
  }

  if (bannerEl) bannerEl.setAttribute('dir', config.dir);
  if (panelEl) panelEl.setAttribute('dir', config.dir);

  bindActions();
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isPanelVisible()) {
      closePanel();
      if (needsBanner()) showBanner();
      return;
    }
    handlePanelKeydown(e);
  });

  if (needsBanner()) {
    showBanner();
  } else {
    applyConsent(config, consent);
  }
}

function readConfig(configEl: HTMLElement): ModuleConfig {
  const wc: FltConsentConfig = window.fltConsentConfig ?? { i18n: {}, dir: 'ltr', cookiePolicyUrl: '' };

  let pixels = [];
  let eventQueue = [];
  try { pixels = JSON.parse(configEl.getAttribute('data-pixels') ?? '[]'); } catch { pixels = []; }
  try { eventQueue = JSON.parse(configEl.getAttribute('data-event-queue') ?? '[]'); } catch { eventQueue = []; }

  return {
    bannerVersion: parseInt(configEl.getAttribute('data-banner-version') ?? '1', 10) || 1,
    consentDuration: parseInt(configEl.getAttribute('data-consent-duration') ?? '15552000', 10) || 15552000,
    bannerPosition: configEl.getAttribute('data-banner-position') ?? 'bottom',
    panelStyle: (configEl.getAttribute('data-panel-style') === 'modal' ? 'modal' : 'panel'),
    consentLogUrl: configEl.getAttribute('data-consent-log-url') ?? '',
    advancedMatching: parseInt(configEl.getAttribute('data-advanced-matching') ?? '0', 10) || 0,
    pixels,
    eventQueue,
    i18n: wc.i18n,
    dir: wc.dir ?? 'ltr',
    cookiePolicyUrl: typeof wc.cookiePolicyUrl === 'string' ? wc.cookiePolicyUrl.trim() : '',
  };
}

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function readCookie(): ConsentMap {
  const raw = getCookie(COOKIE_NAME);
  if (!raw) return { necessary: true };

  let obj: Record<string, unknown>;
  try { obj = JSON.parse(raw); } catch { return { necessary: true }; }

  if (!obj || typeof obj !== 'object') return { necessary: true };
  if (typeof obj.version !== 'number') return { necessary: true };

  return { ...obj, necessary: true } as ConsentMap;
}

function writeCookie(consentObj: ConsentMap): void {
  consentObj.necessary = true;
  consentObj.version = config.bannerVersion;
  consentObj.ts = Math.floor(Date.now() / 1000);

  const value = JSON.stringify(consentObj);
  const expires = new Date(Date.now() + config.consentDuration * 1000).toUTCString();

  const parts = [
    COOKIE_NAME + '=' + encodeURIComponent(value),
    'path=/',
    'expires=' + expires,
    'SameSite=Strict',
  ];
  if (location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

function needsBanner(): boolean {
  return !consent.version || consent.version < config.bannerVersion;
}

// ---------------------------------------------------------------------------
// Consent actions
// ---------------------------------------------------------------------------

function acceptAll(): void {
  consent = { necessary: true };
  for (const cat of CATEGORIES) consent[cat] = true;
  saveConsent();
}

function rejectAll(): void {
  consent = { necessary: true };
  for (const cat of CATEGORIES) consent[cat] = false;
  saveConsent();
}

function savePreferences(): void {
  consent = readToggles();
  saveConsent();
}

function saveConsent(): void {
  writeCookie(consent);
  hideBanner();
  closePanel();
  announceToSR(config.i18n?.consentSaved as string ?? 'Consent preferences saved');
  updateGoogleConsentMode();
  logConsent();
  applyConsent(config, consent);
}

// ---------------------------------------------------------------------------
// Google Consent Mode v2
// ---------------------------------------------------------------------------

const GCM_MAP: Record<string, string[]> = {
  analytics: ['analytics_storage'],
  advertising: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  personalization: ['personalization_storage'],
};

function updateGoogleConsentMode(): void {
  if (typeof gtag !== 'function') return;

  const update: Record<string, string> = {};
  for (const cat in GCM_MAP) {
    const granted = !!consent[cat as keyof ConsentMap];
    for (const signal of GCM_MAP[cat]) {
      update[signal] = granted ? 'granted' : 'denied';
    }
  }

  gtag('consent', 'update', update);
}

// ---------------------------------------------------------------------------
// Consent logging (AJAX — non-blocking)
// ---------------------------------------------------------------------------

function logConsent(): void {
  if (!config.consentLogUrl) return;

  const body = new URLSearchParams();
  body.append('consent', JSON.stringify(consent));
  body.append('banner_version', String(config.bannerVersion));

  fetch(config.consentLogUrl, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    body,
  }).catch(() => { /* best-effort */ });
}

// ---------------------------------------------------------------------------
// Event binding — delegation
// ---------------------------------------------------------------------------

function bindActions(): void {
  document.addEventListener('click', (e: Event) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-flt-tm-action]');
    if (!target) return;

    const action = target.getAttribute('data-flt-tm-action');

    switch (action) {
      case 'accept-all':
        acceptAll();
        break;
      case 'reject-all':
        rejectAll();
        break;
      case 'save-preferences':
        savePreferences();
        break;
      case 'open-panel':
        hideBanner();
        openPanel(consent);
        break;
      case 'close-panel':
        closePanel();
        if (needsBanner()) showBanner();
        break;
    }
  });
}

export { getCookie };
